import React from 'react';
import NProgress from 'nprogress';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import {
  Category,
  Product,
  Tax,
  User,
  Variant,
  Option,
  Company,
} from '../../models';
import { ProductFormFields, reducer } from '../shared/Forms/ProductFormFields';
import { generateVariantsHook } from '../../lib';
import { useNotify } from '../../hooks';
import { validateProductForm } from '../../lib/formValidations';
import { CREATE_PRODUCT, UPDATE_PRODUCT } from '../../graphql/mutation';
import { FormButtons } from '../shared/Forms';
import VariantsForm from '../variant/VariantsForm';

interface ProductFormProps {
  product?: Product;
  categories: Category[];
  taxes: Tax[];
  allies: User[];
  ally?: User;
}

function ProductForm({
  product,
  categories,
  taxes,
  allies,
  ally,
}: ProductFormProps) {
  const notify = useNotify();
  const router = useRouter();

  const [state, dispatch] = React.useReducer(
    reducer,
    product
      ? {
          ...product,
          category: (product?.category as Category)?._id,
          company: product.company ?? '',
        }
      : {
          company: (ally?.company as Company)?._id ?? '',
        }
  );

  const [options, setOptions] = React.useState<Option[]>([]);
  const [variants, setVariants] = React.useState<Variant[]>([]);
  const [disabled, setDisabled] = React.useState(false);

  const updateOptions = React.useCallback(setOptions, [setOptions]);
  const updateVariants = React.useCallback(setVariants, [setVariants]);

  const [updateProductMutation] = useMutation<{
    updateProduct: Product;
  }>(UPDATE_PRODUCT);

  const [createProductMutation] = useMutation<{
    createProduct: Product;
  }>(CREATE_PRODUCT);

  const _variants = React.useMemo(
    () => generateVariantsHook(options, variants),
    [options, variants]
  );

  React.useEffect(() => {
    if (_variants.length !== variants.length) {
      setVariants(_variants);
    }
  }, [_variants, variants]);

  const onSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      if (disabled) return;
      NProgress.start();
      setDisabled(true);

      // filter only active variants
      let variantsData = [...variants.filter((v) => v?.active)];
      // build product obj
      const productData = {
        ...state,
        taxes: (state?.taxes as Tax[]).map((c: Tax) => c?._id),
        priority: parseInt(state?.priority as unknown as string) ?? undefined,
        options: options.map(({ values, name }) => ({ values, name })),
        variants: variantsData,
        __typename: undefined,
      };

      // validate form
      const validationType = product ? 'update' : 'create';
      const error = validateProductForm(productData, validationType);
      if (error) return notify(error, 'error');

      // delete uuid from variants
      variantsData = variantsData.map((v) => {
        delete v._id;
        return v;
      });
      // create or update product
      if (!product) {
        await createProduct({ ...productData, status: 'active' });
      } else {
        await updateProduct(productData);
      }
    } catch (error) {
      notify('Error al crear el producto', 'error');
    } finally {
      setDisabled(false);
      NProgress.done();
    }
  };

  const createProduct = async (productData: Partial<Product>) => {
    try {
      const { data } = await createProductMutation({
        variables: {
          data: productData,
        },
      });
      if (data?.createProduct?._id) {
        notify('Producto creado con éxito', 'success');
        router.push('/app/products');
      } else {
        notify('Ha ocurrido un error al actualizar el producto', 'error');
      }
    } catch (error) {
      notify(`Error al crear el producto: ${error}`, 'error');
    }
  };

  const updateProduct = async (productData: Partial<Product>) => {
    try {
      delete productData.rating;
      delete productData.points;
      delete productData.reviews;
      delete productData.comments;
      delete productData.options;
      delete productData.variants;
      delete productData.slug;

      const { data } = await updateProductMutation({
        variables: {
          data: { ...productData, __typename: undefined },
        },
      });
      if (data?.updateProduct._id) {
        notify('Producto actualizado con éxito', 'success');
        router.push('/app/products');
      } else {
        notify('Ha ocurrido un error al actualizar el usuario', 'error');
      }
    } catch (error) {
      notify(`Error al actualizar el producto: ${error}`, 'error');
    }
  };

  const changeProductStatus = async () => {
    const productData = {
      _id: product?._id,
      active: !state.active,
    };
    await updateProduct(productData);
  };
  return (
    <>
      <form method="POST" className="flex flex-col w-full space-y-10">
        <ProductFormFields
          categories={categories}
          taxes={taxes}
          allies={allies}
          state={state}
          dispatch={dispatch}
          product={product}
          ally={ally}
        />
      </form>
      {!product ? (
        <VariantsForm
          updateOptions={updateOptions}
          updateVariants={updateVariants}
          variants={_variants}
        />
      ) : null}
      <div className="flex justify-end">
        <FormButtons
          type={product ? 'update' : 'create'}
          toggleActive={changeProductStatus}
          active={product?.active}
          disabled={disabled}
          onSubmit={onSubmit}
        />
      </div>
    </>
  );
}

export default ProductForm;
